import { isActiveFilterRow } from './isActiveFilterRow';
import { expectedValueKind, FilterColumnDef, FilterRow, FilterValue, OPERATORS_BY_TYPE } from './types';

export type FilterPreset = { name: string; rows: FilterRow[] };

const VALUE_KINDS = ['none', 'text', 'number', 'numberRange', 'time', 'enum', 'id', 'boolean'];
const COMBINATORS = ['where', 'and', 'or'];
/** 既知の演算子の集合。形状検証で未知の演算子を通すと、列型が分かる前に有効行として扱われてしまう。 */
const ALL_OPERATORS = new Set<string>(
  Object.values(OPERATORS_BY_TYPE).flatMap((ops) => ops.map((o) => o.value as string)),
);

/**
 * 行配列を「連続 AND のグループ」に分け、resolve が null を返した行を落とし、
 * 生き残ったグループの先頭を where / or へ正規化して平坦化する。
 *
 * 単純な filter では OR 境界が壊れる。例: `A(where) / B(or) / C(and)` は `A OR (B AND C)` だが、
 * B だけ落として平坦に詰めると `A(where) / C(and)` = `A AND C` に化ける。
 * グループを保ったまま落とし、残ったグループの先頭に or を復元することで意味を保つ。
 */
export function normalizeFilterRows<T extends { combinator?: unknown }>(
  rows: readonly T[],
  resolve: (row: T) => FilterRow | null,
): FilterRow[] {
  // 型パラメータにしているのは、形状検証（まだ FilterRow と確定していない生データ）でも
  // 同じグループ保全ロジックを使うため。グループ境界は生の combinator から読む。
  const groups: T[][] = [];
  let current: T[] = [];
  rows.forEach((row) => {
    if (row?.combinator === 'or' && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(row);
  });
  if (current.length > 0) groups.push(current);

  const surviving = groups
    .map((group) => group.map(resolve).filter((r): r is FilterRow => r !== null))
    .filter((group) => group.length > 0);

  return surviving.flatMap((group, groupIndex) =>
    group.map((row, rowIndex) => ({
      ...row,
      combinator: rowIndex > 0 ? 'and' : groupIndex === 0 ? 'where' : 'or',
    })),
  );
}

/** ① 形状検証（依存なし・即時）。JSON 形状・判別子・プリミティブ型だけを見る。 */
function isValidValue(v: unknown): v is FilterValue {
  if (typeof v !== 'object' || v === null) return false;
  const value = v as { kind?: unknown; value?: unknown; values?: unknown; min?: unknown; max?: unknown };
  if (typeof value.kind !== 'string' || !VALUE_KINDS.includes(value.kind)) return false;
  switch (value.kind) {
    case 'none': return true;
    case 'text':
    case 'time': return typeof value.value === 'string';
    case 'number': return value.value === null || typeof value.value === 'number';
    case 'numberRange': return (value.min === null || typeof value.min === 'number') && (value.max === null || typeof value.max === 'number');
    case 'enum': return Array.isArray(value.values) && value.values.every((x) => typeof x === 'string');
    case 'id': return Array.isArray(value.values) && value.values.every((x) => typeof x === 'number');
    case 'boolean': return value.value === null || typeof value.value === 'boolean';
    default: return false;
  }
}

/**
 * 行の形状検証。localStorage 由来でもサーバ由来でも、外から来た JSON は同じ関門を通す。
 * 片方だけ緩いと、そちら経由で未知の演算子が入って全件が隠れる。
 */
export function isValidRow(r: unknown): r is FilterRow {
  if (typeof r !== 'object' || r === null) return false;
  const row = r as Partial<FilterRow>;
  return typeof row.id === 'string'
    && typeof row.column === 'string'
    && typeof row.operator === 'string'
    // 未知の演算子を通すと、列型が判明する前（license loading 中）に有効行として
    // 評価され、全件が隠れることがある。既知の集合に含まれるかまで見る。
    && ALL_OPERATORS.has(row.operator)
    && typeof row.combinator === 'string'
    && COMBINATORS.includes(row.combinator)
    && isValidValue(row.value);
}

/**
 * 保存時も有効判定を共有する（設計契約）。無効な行を保存すると、
 * 適用時に消えて「保存した条件と違う」と見える。
 * 単純な filter では OR 境界が壊れるため normalizeFilterRows を通す。
 */
export function normalizePresetRowsForSave(
  rows: FilterRow[],
  columnsByKey: Map<string, FilterColumnDef>,
): FilterRow[] {
  return normalizeFilterRows(rows, (row) => {
    const column = columnsByKey.get(row.column);
    return column && isActiveFilterRow(row, column) ? row : null;
  });
}

type ReadyFlags = { isLicenseReady: boolean; isMastersReady: boolean };

/**
 * ② 列・型整合（license 取得後）と ③ 候補存在確認（マスタ取得後）。
 * 依存が揃っていない段階では、その検証を行わず値をそのまま保持する
 * （空の候補で検証すると正しい保存済み条件を消してしまうため）。
 */
export function resolvePreset(
  preset: FilterPreset,
  columns: FilterColumnDef[],
  { isLicenseReady, isMastersReady }: ReadyFlags,
): { rows: FilterRow[]; droppedCount: number } {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  let dropped = 0;

  // グループを保ったまま落とし、生き残ったグループの先頭を where / or へ正規化する。
  const rows = normalizeFilterRows(preset.rows, (row) => {
    const column = byKey.get(row.column);

    if (!isLicenseReady) return row;            // ② 未実施（任意項目の列がまだ生成されていない）
    if (!column) { dropped += 1; return null; }

    const allowed = OPERATORS_BY_TYPE[column.type].map((o) => o.value);
    if (!allowed.includes(row.operator)) { dropped += 1; return null; }
    if (row.value.kind !== expectedValueKind(column.type, row.operator)) { dropped += 1; return null; }

    if (!isMastersReady) return row;            // ③ 未実施（候補がまだ空）

    if (row.value.kind === 'enum' && column.type === 'enumSelect') {
      const valid = new Set(column.options.map((o) => o.value));
      const values = row.value.values.filter((v) => valid.has(v));
      if (values.length === 0) { dropped += 1; return null; }
      return { ...row, value: { kind: 'enum', values } };
    }
    if (row.value.kind === 'id' && column.type === 'idSelect') {
      const valid = new Set(column.options.map((o) => o.value));
      const values = row.value.values.filter((v) => valid.has(v));
      if (values.length === 0) { dropped += 1; return null; }
      return { ...row, value: { kind: 'id', values } };
    }
    return row;
  });

  return { rows, droppedCount: dropped };
}
